// proposal-scope-guard.ts
//
// Luật cho `allowed_paths` mà một proposal tự khai. Tách khỏi
// proposal-applier.ts (257 dòng, quá ngân sách 200) — applier giữ phần giao
// dịch, file này giữ phần "worker được khai phạm vi tới đâu".

import { coversAControllerOwnedPath, isControllerOwnedPath } from "./controller-owned-paths.js";
import { matchesGlob, pathAllowed } from "./result-verifier-scope-checks.js";
import { SENSITIVE_CI_HOOK_GLOBS } from "./sensitive-path-globs.js";

// P22: `allowed_paths` is the boundary every later guard measures this
// ticket's work against — the verifier's scope check reads it, and the
// Edit-family adapters (implement/testcase/test) are bounded by NOTHING
// else. Letting a proposal author that field is the governed party writing
// the rule it will be judged by: the Bug K/Bug L shape, and measurably the
// first link of a real chain — a ticket scoped `**` gets Edit-family, and
// `.avatar/verification-profile.json` (the recipe list `checkRecipeIsApproved`
// honours, re-read live each tick) was inside the worktree and matched by
// that same `**`.
//
// Three rules, all narrow on purpose. An unbounded scope is refused outright;
// so is a git-magic pathspec (leading `:`, review 260930 M1) — the verifier
// reads it as literal text matching no file while `git add` reads `:!x` as
// "everything but x" and `:/` as the repo root, so the two readers disagree
// about how wide it is; a scope reaching a controller-owned path is refused by
// the SAME predicate the verifier uses (controller-owned-paths.ts), so the two
// can never drift into disagreeing about what a worker may claim. Everything
// else — ordinary
// `src/**`, `test/**`, explicit file lists — passes untouched, because a
// plan worker proposing normally-scoped tickets is the mission's main path
// and a guard that blocks it is a guard someone will delete.
const UNBOUNDED_SCOPE_PATTERNS = new Set(["**", "*", "/", "./", ".", "**/*"]);

// Representative CI/hook files (review-pr beta.6 H2 residual): the probe used
// to be `<dir>/any-file` + the glob text, which `**/*.yaml` and
// `**/workflows/**` slipped past. Each sample is a real spelling a repo may
// carry, both `.yml` and `.yaml`, so a glob reaching CI through ANY extension
// or subdirectory is caught by the same `pathAllowed` the verifier reads.
const CI_HOOK_SAMPLE_PATHS: readonly string[] = [
  ".gitlab-ci.yml",
  ".gitlab-ci.yaml",
  ".gitlab/ci/build.yml",
  ".gitlab/ci/build.yaml",
  ".github/workflows/ci.yml",
  ".github/workflows/ci.yaml",
  ".github/dependabot.yml",
  ".husky/pre-commit",
  ".pre-commit-config.yaml",
  "lefthook.yml",
  ".git/hooks/pre-commit",
];

// Spellings of one `allowed_paths` entry to probe. `{a,b}` is a literal to
// both readers (matchesGlob / hook `_match_glob`) and `**/x` never matches
// root `x` there, so neither covers a root CI file — but a shell reads the
// brace wide and a worker reads `**/.husky/**` as "every .husky". A scope
// two readers disagree on is refused like a git-magic pathspec (M1), by
// probing every alternative, as `isSensitivePath` does for `**/`.
function scopeSpellings(pattern: string): string[] {
  const [, head = "", alts, tail = ""] = /^(.*)\{([^{}]*)\}(.*)$/.exec(pattern) ?? [];
  const expanded =
    alts === undefined ? [pattern] : alts.split(",").map((alt) => `${head}${alt}${tail}`);
  return expanded.flatMap((p) => (p.startsWith("**/") ? [p, p.slice(3)] : [p]));
}

// 06g R3 (audit H2): a pattern that IS a CI/hook path (`.gitlab-ci.yml`,
// `.github/workflows/ci.yml`) or COVERS one (`.husky/**`, `.github`,
// `**/*.yaml`) — judged with the verifier's own `pathAllowed` so the two
// readers agree, exactly as `coversAControllerOwnedPath` does. Only a person
// grants CI scope, through a `redirect` amend (directive-redirect-application.ts
// never calls this guard).
function isOrCoversCiHookPath(pattern: string): boolean {
  const spellings = scopeSpellings(pattern);
  return (
    SENSITIVE_CI_HOOK_GLOBS.some((glob) => spellings.some((s) => matchesGlob(glob, s))) ||
    CI_HOOK_SAMPLE_PATHS.some((sample) => pathAllowed(sample, spellings))
  );
}

/** Rejection reason for a proposal-authored `allowed_paths`, or undefined when the scope is acceptable. */
export function checkProposedScope(allowedPaths: string[] | undefined): string | undefined {
  if (allowedPaths === undefined) return undefined;
  for (const pattern of allowedPaths) {
    const trimmed = pattern.trim();
    if (UNBOUNDED_SCOPE_PATTERNS.has(trimmed)) {
      return (
        `proposal khai allowed_paths "${pattern}" — phạm vi không giới hạn. ` +
        'Ticket do worker đề xuất phải khai phạm vi cụ thể (vd "src/**"); ' +
        "mở toàn repo là tự cấp quyền cho chính mình, cần người duyệt."
      );
    }
    if (trimmed.startsWith(":")) {
      return (
        `proposal khai allowed_paths "${pattern}" — pathspec đặc biệt của git (bắt đầu bằng ":"). ` +
        'Verifier đọc nó như chữ thường không khớp file nào, git lại hiểu ":!x" là "mọi thứ trừ x" ' +
        'và ":/" là gốc repo ⇒ phạm vi không đo được. Khai đường dẫn/glob cụ thể (vd "src/**").'
      );
    }
    // A pattern is refused when it can reach a controller-owned path — both
    // the literal path and the glob that would match it.
    if (isControllerOwnedPath(trimmed) || coversAControllerOwnedPath(trimmed)) {
      return (
        `proposal khai allowed_paths "${pattern}" chạm đường của controller ` +
        "(binding/verification-profile/PLAN.md/.avatar/tickets) — worker không được sửa thứ nó bị kiểm bằng."
      );
    }
    if (isOrCoversCiHookPath(trimmed)) {
      return (
        `proposal khai allowed_paths "${pattern}" chạm CI/hook của repo ` +
        "(.gitlab-ci.yml, .gitlab/, .github/, .husky/, .pre-commit-config.yaml, .git/) — " +
        "worker không đề xuất phạm vi CI/hook; người cấp qua `redirect` amend."
      );
    }
  }
  return undefined;
}
